/**
 * 접속 중인 호스트명을 보고 user-api 서버의 베이스 URL을 고른다.
 *
 * `NEXT_PUBLIC_*` 환경변수를 쓰지 않는 이유는 이 앱이 `output: "export"`로 정적 빌드되기 때문이다.
 * 같은 산출물이 dq.ai.kr 과 haebom.ai.kr(그리고 각 dev) 네 호스트에 그대로 배포되므로,
 * 빌드 시점 환경변수 대신 런타임에 `window.location.hostname`을 읽어 분기한다.
 *
 * 도메인 계열(dq / haebom)과 환경(dev / prod)을 서로 넘나들지 않는다. 각 FE 호스트는 정확히 하나의 API 호스트만 부르고,
 * 백엔드 CORS 도 요청 Host 별로 같은 짝만 허용한다. 새 도메인을 붙이면 아래 표와 백엔드
 * `security.cors-allow-domains-by-host` 를 함께 고친다.
 */

/** FE 호스트 → user-api 호스트. 운영 도메인은 반드시 여기 있어야 한다 — 표에 없는 호스트는 dev API 로 간다. */
const API_BY_HOST: Record<string, string> = {
  "haebom.ai.kr": "https://api.haebom.ai.kr",
  "dev.haebom.ai.kr": "https://api.dev.haebom.ai.kr",
  "dq.ai.kr": "https://api.dq.ai.kr",
  "dev.dq.ai.kr": "https://api.dev.dq.ai.kr",
};

/**
 * 표에 없는 호스트(localhost, 미리보기)의 기본 API. **dev 서버다.**
 *
 * 운영 도메인은 위 표에서 매칭돼 이 값에 도달하지 않는다(`API_BY_HOST[hostname]` 이 먼저 반환).
 * 바꿔 말하면 운영 도메인을 새로 붙이면서 표에 넣지 않으면 그 도메인이 조용히 dev 를 보게 되므로,
 * 도메인 추가 시 표와 백엔드 CORS 를 함께 고쳐야 한다.
 *
 * `api.dev.dq.ai.kr` 를 쓰면 안 된다. ALB 가 그 호스트를 `api.dev.haebom.ai.kr` 로 301 하는데,
 * CORS preflight(OPTIONS)는 리다이렉트를 따라가지 않아 브라우저가 ERR_INVALID_REDIRECT 로 끊는다.
 * 최종 목적지를 직접 부른다.
 */
//const DEFAULT_DEV_API = "https://api.dev.dq.ai.kr";
const DEFAULT_DEV_API = "https://api.dev.haebom.ai.kr";

/**
 * @returns 접속 호스트에 짝지어진 user-api 베이스 URL. 서버 사이드(`window` 없음)나 표에 없는 호스트에서는
 *   `BASE_URL` 환경변수가 있으면 그 값, 없으면 dev API(`DEFAULT_DEV_API`).
 */
export const getApiBaseUrl = (): string => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();
    const mapped = API_BY_HOST[hostname];
    if (mapped) return mapped;

    // 운영 도메인이 아닐 때만 호스트 분기 확인용 로그를 남긴다(debug 레벨, 콘솔 Verbose에서 보임).
    console.debug("호스트이름", hostname);
    return process.env.BASE_URL || DEFAULT_DEV_API;
  }

  // 서버 사이드 기본값
  return process.env.BASE_URL || DEFAULT_DEV_API;
};
